import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useRootStore } from "store";
import ExternalUsers from "views/wizard/components/ExternalUsers/ExternalUsers";
import InternalUsers from "views/wizard/components/InternalUsers/InternalUsers";
import CustomerInformation from "views/wizard/components/CustomerInformation/CustomerInformation";
import CustomerService from "views/wizard/components/CustomerService/CustomerService";
import UserService from "views/wizard/components/UserService/UserService";
import InviteExternalUsers from "views/wizard/components/inviteExternalUsers/InviteExternalUsers";
import WizardStep from "views/wizard/components/WizardStep";
import Modal from "components/modal/Modal";
import { Icon } from "components/icon/Icon";
import MKLoader from "components/loader/MKLoader";
import { captureEvent } from "components/posthog/customEventCapture";
import { ExternalUser, ExternalUserRole } from "models/customers/customers";

import "./Wizard.scss";

export default function Wizard() {
  const navigate = useNavigate();

  const {
    isSaving,
    hasErrors,
    customer,
    fetchCustomer,
    addCustomerUserToCustomer,
    initUserServices,
    removeCustomerUserFromCustomer,
    resetValidUserData,
    resetWizard,
    setFpService,
    setUserFpService,
    toggleInviteAllExternalUsers,
    toggleInviteExternalUser,
    toggleDocumentServices,
    toggleUserDocumentService,
    toggleUserOnManyDocumentServices,
    saveCustomer
  } = useRootStore((state) => state.wizard);
  const {
    internalUsers,
    externalUsers,
    fpService: fpConfig,
    documentServices
  } = customer;

  const { fetchCustomers } = useRootStore((state) => state.customers);

  const { resetCustomer } = useRootStore((state) => state.global);

  const { id } = useParams();

  const [step, setStep] = useState(0);
  const [showWarning, setShowWarning] = useState(!!customer.reactivation);

  useEffect(() => {
    if (id) {
      fetchCustomer(id);
      resetCustomer();
    }
  }, [fetchCustomer, id, resetCustomer]);

  useEffect(() => {
    if (!isSaving) {
      if (!hasErrors) {
        fetchCustomers(false);
        navigate("/dashboard");
      }
    }

    if (!customer.reactivation && customer.reactivation) {
      setShowWarning(true);
    }
  }, [customer.reactivation, fetchCustomers, hasErrors, isSaving, navigate]);

  useEffect(() => {
    return () => {
      resetWizard();
    };
  }, [resetWizard]);

  const handleNextClick = () => {
    if (step === 0 && !verifyOwnerConstraint()) {
      return;
    }

    setStep(step + 1);
  };

  const handleUserServiceToggle = (
    userId: string,
    serviceId: string,
    value: boolean
  ) => {
    toggleUserDocumentService(userId, serviceId);
  };

  const handleChildUserServicesToggle = (
    userId: string,
    serviceId: string[],
    active: boolean
  ) => {
    toggleUserOnManyDocumentServices(userId, serviceId, active);
  };

  const handleBackClick = () => {
    setStep(step - 1);
  };

  const verifyOwnerConstraint = () => {
    if (
      externalUsers.length > 0 &&
      externalUsers.filter((u) => u.roles.includes(ExternalUserRole.Admin)).length === 0
    ) {
      return false;
    }
    return true;
  };

  const handleCreateClick = () => {
    saveCustomer();
  };

  const closeModal = () => {
    setShowWarning(false);
  };

  const handleToggleChildServices = (ids: string[], enabled: boolean) => {
    toggleDocumentServices(ids, enabled);
  };

  const handleAddCustomerUser = (user: Partial<ExternalUser>) => {
    addCustomerUserToCustomer(user, user.roles);
  };

  const handleRemoveCustomerUserFromCustomer = (toggleId: string) => {
    removeCustomerUserFromCustomer(toggleId);
  };

  const handleInviteExternalUser = (email: string, invite: boolean) => {
    toggleInviteExternalUser(email, invite);
  };

  const handleInviteAllExternalUsers = (invite: boolean) => {
    toggleInviteAllExternalUsers(invite);
  };

  const handleInitUserServices = () => {
    initUserServices();
  };

  const navigateToStep = (newStep: number, title: string) => {
    if (newStep === 0 && !verifyOwnerConstraint()) {
      return;
    }
    captureEvent(title);
    setStep(newStep);
  };

  const handleSetFpService = (customerChoice: string) => {
    setFpService(customerChoice);
  };

  const handleUserSetFpService = (customerChoice: string, userId: string) => {
    setUserFpService(customerChoice, userId);
  };

  const handleResetValidUserData = () => {
    resetValidUserData();
  };

  const renderText = (header: string, paragraphs: string[]) => {
    const ps: ReactNode[] = [];
    for (let i = 0; i < paragraphs.length; i++) {
      ps.push(<p key={i}>{paragraphs[i]}</p>);
    }
    return [<h1 key="modH">{header}</h1>, ...ps];
  };

  const createModal = () => {
    if (showWarning) {
      return (
        <Modal modalTitle="warning" onDismiss={closeModal}>
          <div className="modalText">
            {renderText("Viktigt meddelande!", [
              "Du återöppnar en tidigare avslutad kund, alla meddelanden och dokument finns kvar. ",
              "Säkerställ att det är okej att kundanvändare som läggs till får tillgång till eventuella dokument som ligger på kunden."
            ])}
            <button
              className="primaryButton"
              type="button"
              onClick={closeModal}
            >
              Ok
            </button>
          </div>
        </Modal>
      );
    }
  };

  const renderWizardSteps = () => {
    const wizardSteps = [
      { title: "1. Uppgifter kund & kundteam", step: 0 },
      { title: "2. Digitala tjänster & dokument", step: 1 },
      { title: "3. Behörigheter & tillgång till tjänster", step: 2 },
      { title: "4. Bekräfta & bjud in", step: 3 }
    ];

    return wizardSteps.map((currentStep) => (
      <li
        onClick={() => navigateToStep(currentStep.step, currentStep.title)}
        className={
          step === currentStep.step ? "activeWizardStep" : "wizardStep"
        }
      >
        {currentStep.title}
      </li>
    ));
  };

  const inviteButtonText =
    externalUsers.filter((user) => user.id === undefined).length > 0
      ? "Bjud in & skapa kund"
      : "Skapa kund";

  return (
    <>
      <div className="wizard box">
        <ul>{renderWizardSteps()}</ul>
        <div>
          <WizardStep active={step === 0}>
            <>
              <div className="info">
                Info: Välkommen! Här kontrollerar du kundens uppgifter och
                lägger till kundanvändare. När du är klar med Kunduppgifter
                &amp; användare, klickar du på Nästa.
              </div>
              <CustomerInformation
                customer={customer}
                collapsed={false}
                info="Kontroller att kunduppgifterna stämmer. Kunduppgifterna hämtas ifrån MittLudvig och om du behöver ändra något så gör du det i MittLudvig. Kort därefter kommer uppdateringen att synas i MittKontor."
              />
              <ExternalUsers
                users={externalUsers}
                collapsed={false}
                onItemAdded={handleAddCustomerUser}
                onItemRemove={handleRemoveCustomerUserFromCustomer}
                validUserData={customer.validUserData}
                resetValidUserData={handleResetValidUserData}
                info="Kundanvändare är de personer från kundföretaget som har inloggning till MittKontor. Förutom firmatecknare kan det t ex vara andra delägare, anställda, revisorer eller bankkontakt. Beställning av fler kundanvändare ska ske i ett meddelande från firmatecknare i MittKontor. När du lägger upp en ny kundanvändare anges e-post, personnummer, förnamn och efternamn. Klicka sedan på Lägg till användare. Om kundanvändaren är Firmatecknare bockar du i Firmatecknare. Har du fler kundanvändare som ska registreras upprepar du processen. Gå sedan vidare för att sätta rätt behörigheter och skicka inbjudan till Kundanvändare. Alla kunder ska ha en Kundanvändare för att kunna hämta sitt material."
              />
              <InternalUsers collapsed={false} />
            </>
          </WizardStep>
          <WizardStep active={step === 1}>
            <CustomerService
              fpConfig={fpConfig}
              onSetFpService={handleSetFpService}
              services={documentServices}
              collapsed={false}
              onToggleChildServices={handleToggleChildServices}
            />
          </WizardStep>
          <WizardStep onInit={handleInitUserServices} active={step === 2}>
            <>
              <UserService
                isInternal={false}
                summaryTitle="Tillgång till tjänster: Kundanvändare"
                editTitle="Redigera kundanvändare"
                users={externalUsers}
                collapsed={false}
                fpConfig={fpConfig}
                onSetFpService={handleUserSetFpService}
                onToggleService={handleUserServiceToggle}
                onToggleChildServices={handleChildUserServicesToggle}
              />
              <UserService
                isInternal={true}
                summaryTitle={
                  "Tillgång till tjänster: Kundteam Ludvig &amp; Co"
                }
                editTitle={"Redigera Kundteam Ludvig &amp; Co"}
                users={internalUsers}
                collapsed={false}
                fpConfig={fpConfig}
                onSetFpService={handleUserSetFpService}
                onToggleService={handleUserServiceToggle}
                onToggleChildServices={handleChildUserServicesToggle}
              />
            </>
          </WizardStep>
          <WizardStep active={step === 3}>
            <>
              <div className="info">
                Info: Toppen! Nu är det bara att kontrollera att uppgifterna som
                du har angett stämmer. Vill du redigera någon av uppgifterna,
                klicka på respektive flik längst upp på sidan och ändra de
                uppgifter du vill redigera. När du har Skapat kund kommer du
                till startsidan Mina kunder där kunden nu ligger i din lista.
              </div>
              <CustomerInformation customer={customer} collapsed={false} />
              <ExternalUsers
                users={externalUsers}
                collapsed
                onItemAdded={handleAddCustomerUser}
                onItemRemove={handleRemoveCustomerUserFromCustomer}
              />
              <InternalUsers collapsed />
              <UserService
                isInternal={false}
                summaryTitle="Tillgång till tjänster: Kundanvändare"
                editTitle="Redigera kundanvändare"
                fpConfig={fpConfig}
                collapsed
                onSetFpService={handleUserSetFpService}
                onToggleService={handleUserServiceToggle}
                onToggleChildServices={handleChildUserServicesToggle}
              />
              <UserService
                isInternal={true}
                summaryTitle="Tillgång till tjänster: Konsulter"
                editTitle="Redigera kundanvändare"
                fpConfig={fpConfig}
                collapsed
                onSetFpService={handleUserSetFpService}
                onToggleService={handleUserServiceToggle}
                onToggleChildServices={handleChildUserServicesToggle}
              />
              <InviteExternalUsers
                externalUsers={externalUsers}
                onInviteUser={handleInviteExternalUser}
                onInviteAllUsers={handleInviteAllExternalUsers}
                isWizard={true}
              />
            </>
          </WizardStep>
          <div className="buttonContainer">
            <button
              className="backButton"
              onClick={handleBackClick}
              style={{ display: step > 0 ? "block" : "none" }}
            >
              <Icon name={"angle-left"} size="lg" /> Tillbaka
            </button>
            <button
              className="forwardButton"
              onClick={handleNextClick}
              style={{ display: step < 3 ? "block" : "none" }}
            >
              Nästa <Icon name={"angle-right"} size="lg" />
            </button>
            <button
              className="primaryButton"
              onClick={handleCreateClick}
              style={{ display: step === 3 ? "block" : "none" }}
            >
              {inviteButtonText}
            </button>
          </div>
        </div>
        <MKLoader show={isSaving} message="Skapar kund..." />
      </div>
      {createModal()}
    </>
  );
}